<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <h1>{{ order.seq_number ?? order.code ?? "-" }}</h1>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <dl class="row">
          <dt class="col-sm-4">{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK") }}</dt>
          <dd class="col-sm-8">
            <span v-if="order.week_starts_at">
              {{ $formatDate(order.week_starts_at, "LL") }}
              &rarr;
              {{ order.week_ends_at ? $formatDate(order.week_ends_at, "LL") : "-" }}
            </span>
            <span v-else>-</span>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATUS") }}</dt>
          <dd class="col-sm-8">
            <establishment-internal-order-status-badge
              v-if="order.status"
              :order="order"
            />
            <span v-else>-</span>
          </dd>
        </dl>

        <dl class="row" v-if="order.invoicing_status">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS") }}
          </dt>
          <dd class="col-sm-8">
            <badge :type="invoicingStatusBadgeType">
              {{
                $t(
                  `ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS_${order.invoicing_status}`
                )
              }}
            </badge>
          </dd>
        </dl>

        <dl class="row" v-if="order.submitted_at">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.SUBMITTED_AT") }}
          </dt>
          <dd class="col-sm-8">{{ $timeZoneDateFormat(order.submitted_at) }}</dd>
        </dl>
        <dl class="row" v-if="order.submittedBy">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.SUBMITTED_BY") }}
          </dt>
          <dd class="col-sm-8">
            <object-link :object="order.submittedBy" />
          </dd>
        </dl>
        <dl class="row" v-if="order.accepted_at">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.CONFIRMED_AT") }}
          </dt>
          <dd class="col-sm-8">{{ $timeZoneDateFormat(order.accepted_at) }}</dd>
        </dl>
        <dl class="row" v-if="order.acceptedBy">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.CONFIRMED_BY") }}
          </dt>
          <dd class="col-sm-8">
            <object-link :object="order.acceptedBy" />
          </dd>
        </dl>

        <dl class="row" v-if="order.sent_at">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.MAIL_SENT_AT") }}
          </dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(order.sent_at) }}
            <div class="text-muted small" v-if="sentRecipients">
              {{ sentRecipients }}
            </div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.TOTAL") }}</dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(order?.pricing?.total ?? 0) }}
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT") }}</h2>
          </dt>
          <dd class="col-sm-8">
            <object-link
              v-if="order.sourceEstablishment"
              :object="order.sourceEstablishment"
            />
            <span v-else>{{ order.source_establishment_id || "-" }}</span>
            <div class="text-muted" v-if="order.sourceCafeteria">
              {{ order.sourceCafeteria.name }}
            </div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.SATELLITE_ESTABLISHMENT") }}</h2>
          </dt>
          <dd class="col-sm-8">
            <object-link
              v-if="order.destinationEstablishment"
              :object="order.destinationEstablishment"
            />
            <span v-else>{{ order.destination_establishment_id || "-" }}</span>
            <div class="text-muted" v-if="order.destinationCafeteria">
              {{ order.destinationCafeteria.name }}
            </div>
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row" v-if="order.organization">
          <dt class="col-sm-4">{{ $t("COMMON.ORGANIZATION") }}</dt>
          <dd class="col-sm-8">
            <organization :organization="order.organization" />
          </dd>
        </dl>

        <dl class="row" v-if="order.created_at">
          <dt class="col-sm-4">{{ $t("COMMON.CREATED_AT") }}</dt>
          <dd class="col-sm-8">{{ $timeZoneDateFormat(order.created_at) }}</dd>
        </dl>

        <dl class="row" v-if="order.excerpt">
          <dt class="col-sm-4">{{ $t("COMMON.NOTE") }}</dt>
          <dd class="col-sm-8">
            <div v-html="order.excerpt"></div>
          </dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import EstablishmentInternalOrderStatusBadge from "./EstablishmentInternalOrderStatusBadge.vue";
import {
  ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_INVOICED,
  ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_PARTIALLY_INVOICED,
} from "@/constants/establishmentInternalOrders";

export default {
  name: "establishment-internal-order-view-global",
  components: { EstablishmentInternalOrderStatusBadge },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  computed: {
    /**
     * Flattened list of the addresses the order was e-mailed to.
     */
    sentRecipients() {
      const sentTo = this.order.sent_to || {};
      return [...(sentTo.to || []), ...(sentTo.cc || []), ...(sentTo.bcc || [])].join(
        ", "
      );
    },
    invoicingStatusBadgeType() {
      switch (this.order.invoicing_status) {
        case ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_INVOICED:
          return "success";
        case ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_PARTIALLY_INVOICED:
          return "info";
        default:
          return "primary";
      }
    },
  },
};
</script>
